import intl from 'react-intl-universal'
import React, { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import {
  getCustomWords,
  deleteCustomWords,
  addCustomWords,
} from '~/api/spellcheck'

const wordsRegex = /([!`\-_."'#%:;<>=@{}~$()*+/\\?[\]^|]+)/

interface Data {
  words: string[]
  error?: string
}

export const ManageCustomWordsModal = ({ setIsOpen, isOpen }) => {
  const [data, setData] = useState<Data | null>()
  const [words, setWords] = useState<string[] | undefined>([])
  const [deleted, setDeleted] = useState(false)
  const [checkedOptions, setCheckedOptions] = useState<string[]>([])
  const [inputError, setInputError] = useState('')
  const [customWords, setCustomWords] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const getWords = async () => {
      const response = await getCustomWords()
      setData(response)
      setWords(response?.words || [])
    }

    if (isOpen) {
      getWords()
    }
  }, [isOpen])

  const toogleModal = () => {
    if (isOpen) {
      setData(null)
    }

    setIsOpen()
  }

  const handleChange = (event) => {
    if (deleted) {
      setDeleted(false)
    }
    const { target } = event
    const checked = target.checked
    const option = target.value

    if (checked) {
      setCheckedOptions([...checkedOptions, option])
    } else {
      setCheckedOptions(checkedOptions.filter((o) => o !== option))
    }
  }

  const handleDeleteWords = async () => {
    setIsLoading(true)
    const response = await deleteCustomWords(checkedOptions)
    if (response) {
      setIsLoading(false)
      const filteredWords = data
        ? data.words.filter((word) => !response?.words.includes(word))
        : []

      setData({ words: filteredWords })
      setWords(filteredWords)
      setDeleted(response?.deleted_words)
      setCheckedOptions([])
    }
  }

  const handleCustomWordsSearch = (e) => {
    const value = e.target.value

    let filteredWords = data?.words.filter((word) =>
      word.toUpperCase().includes(value.toUpperCase())
    )

    if (filteredWords?.length === 0) {
      filteredWords = [
        intl.get(
          'settings.spellchecker.modal.manageCustomWordsModal.searchEmpty'
        ),
      ]
    }
    setWords(filteredWords)
  }

  const handleCustomWords = (event) => {
    if (inputError) {
      setInputError('')
    }
    setCustomWords(event.target.value)
  }

  const handleAddCustomWords = async () => {
    if (customWords && !wordsRegex.test(customWords)) {
      setIsLoading(true)
      const response = await addCustomWords(customWords)
      if (response) setIsLoading(false)
      if (response?.new_words?.length > 0) {
        if (words) {
          setWords([...words, ...response?.new_words])
        } else {
          setWords([...response?.new_words])
        }

        if (data) {
          setData({ words: [...data.words, ...response?.new_words] })
        } else {
          setData({ words: [...response?.new_words] })
        }
      }
      setCustomWords('')
    } else {
      setInputError(intl.get('SpellcheckSettings.customWords.error'))
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toogleModal}
      centered
      data-testid="manage-custom-words-modal"
      className="customWordsModal"
    >
      <ModalHeader className="manageWordsModalHeader" toggle={setIsOpen}>
        <span className="modalTitle">
          {intl.get('settings.spellchecker.manageCustomWordsButton')}
        </span>
        <span className="modalSubTitle">
          {intl.get(
            'settings.spellchecker.modal.manageCustomWordsModal.subtitle'
          )}
        </span>
      </ModalHeader>
      <ModalBody>
        {data?.words && (
          <div className="input-group mt-1 dataContainer">
            <div className="mb-2 addCustomWords">
              <input
                id="custom-words"
                data-testid="custom-words"
                className={`form-control searchInput ${
                  inputError ? 'inputError' : ''
                }`}
                type="text"
                placeholder={intl.get(
                  'settings.spellchecker.customWordsDescription'
                )}
                value={customWords}
                onChange={handleCustomWords}
              />
              <input
                className="btn btn-md btn-secondary ml-2 ng-binding addWords"
                type="button"
                value={intl.get(
                  'settings.spellchecker.modal.manageCustomWordsModal.addWords'
                )}
                id="add-custom-word"
                data-testid="add-custom-word"
                disabled={!customWords || isLoading}
                onClick={handleAddCustomWords}
              />
            </div>
            <input
              type="text"
              className="form-control searchInput"
              id="custom-words-search-input"
              onChange={handleCustomWordsSearch}
              placeholder={intl.get(
                'settings.spellchecker.modal.manageCustomWordsModal.search'
              )}
              aria-label={intl.get(
                'settings.spellchecker.modal.manageCustomWordsModal.search'
              )}
              aria-describedby="input-search-custom-words"
            />
            <div className="mt-4 wordsDiv">
              {words
                ?.slice()
                .reverse()
                .map((word, idx) => {
                  return (
                    <div key={idx} className="wordsCheck">
                      {word !==
                        intl.get(
                          'settings.spellchecker.modal.manageCustomWordsModal.searchEmpty'
                        ) && (
                        <input
                          type="checkbox"
                          className="mr-2"
                          name={word}
                          value={word}
                          checked={checkedOptions.includes(word)}
                          onChange={handleChange}
                        />
                      )}
                      {word}
                    </div>
                  )
                })}
            </div>
          </div>
        )}
        {!data && <div>{intl.get('Spellcheck.modal.loading')}</div>}
        {data?.error && <div className="inputErrorMessage">{data?.error}</div>}
      </ModalBody>
      <ModalFooter>
        {inputError && (
          <div className="inputErrorMessage">
            <span>{inputError}</span>
          </div>
        )}
        {deleted && (
          <span className="successDeleted">
            {intl.get(
              'settings.spellchecker.modal.manageCustomWordsModal.deleteSuccess'
            )}
          </span>
        )}
        <Button
          color="danger"
          disabled={checkedOptions.length === 0 || isLoading}
          onClick={handleDeleteWords}
        >
          {intl.get(
            'settings.spellchecker.modal.manageCustomWordsModal.deleteButton',
            {
              count: checkedOptions.length === 0 ? '' : checkedOptions.length,
            }
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
